import { maybe } from '@passionware/monads';
import { Capability, FeatureFlag } from '__types__';
import Account from 'lib/auth/Account';

/**
 * Check if a feature flag is enabled in the active feature flags list.
 */
export const hasFlag = (
  featureFlags: FeatureFlag[],
  requiredFlag: FeatureFlag
): boolean => {
  return featureFlags?.includes(requiredFlag) ?? false;
};

/**
 * Check if at least one of the provided feature flags is enabled.
 */
export const hasFlagOneOf = (
  featureFlags: FeatureFlag[],
  requiredFlags: FeatureFlag[]
): boolean => {
  return requiredFlags.some(flag => hasFlag(featureFlags, flag));
};

/**
 * Create a capability checker function with a custom superuser validator.
 */
export function createHasCapability(isSuperUser: () => boolean) {
  return (
    capabilities: Partial<Record<Capability, boolean>>,
    requiredCapability: Capability
  ): boolean => {
    return isSuperUser() || Boolean(capabilities[requiredCapability]);
  };
}

/**
 * Create an overall access checker with a custom superuser validator.
 */
export function createCheckOverallAccess(isSuperUser: () => boolean) {
  const hasCapability = createHasCapability(isSuperUser);

  return (
    requiredFlag: FeatureFlag | null = null,
    requiredCapabilities: Capability | Capability[] | null = null,
    requiredCapabilitiesAtLeastOne: Capability[] | null = null,
    activeFeatureFlags: FeatureFlag[] = [],
    activeCapabilities: Partial<Record<Capability, boolean>> = {}
  ): boolean => {
    // Normalize inputs using maybe
    const flagProvided = maybe.isPresent(requiredFlag);
    const allCaps = Array.isArray(requiredCapabilities)
      ? requiredCapabilities
      : requiredCapabilities
        ? [requiredCapabilities]
        : [];
    const anyCaps = requiredCapabilitiesAtLeastOne ?? [];

    const featureEnabled = flagProvided
      ? hasFlag(activeFeatureFlags, requiredFlag as FeatureFlag)
      : true;

    if (!featureEnabled) {
      return false;
    }

    // Flag-only access (no capabilities specified)
    const noCapsSpecified =
      maybe.isAbsent(maybe.fromArray(allCaps)) &&
      maybe.isAbsent(maybe.fromArray(anyCaps));

    if (flagProvided && noCapsSpecified) {
      return true;
    }

    // Superuser bypass (after feature flag check)
    if (isSuperUser()) {
      return true;
    }

    // All capabilities required (AND logic)
    if (allCaps.length > 0) {
      return allCaps.every(cap => hasCapability(activeCapabilities, cap));
    }

    // At least one capability required (OR logic)
    if (anyCaps.length > 0) {
      return anyCaps.some(cap => hasCapability(activeCapabilities, cap));
    }

    // No flag and no capabilities = deny access
    return false;
  };
}

export const hasCapability = createHasCapability(Account.isSuperUser);
export const checkOverallAccess = createCheckOverallAccess(Account.isSuperUser);
